/**
 * ATHÉR - zoom inside the product image frame (replaces Horizon's full-screen
 * lightbox by default; blocks/_product-media-gallery.liquid "Zoom inside the frame").
 *
 * The frame keeps its size and place; only the image scales inside it
 * (overflow hidden), so nothing moves on the page. Click, Enter or Space zooms
 * in to the chosen scale (1.5x by default) around the pointer, or the centre
 * from the keyboard; the zoom point follows the mouse; a second click, Enter,
 * Space or Escape returns to normal. Touch: one tap zooms, a drag pans inside
 * the frame (the page and the carousel stay still), a second tap resets; when
 * not zoomed, swiping between images works as before. Arrow keys pan while
 * zoomed. Changing image, a variant change (the section re-renders) or the
 * gallery leaving the screen resets it. The image is `contain` at rest.
 */
(function () {
  'use strict';

  var CONFIG = 'script[type="application/json"][data-ather-zoom]';

  function setup(cfgEl) {
    var gallery = cfgEl.closest('.product-information__media') || cfgEl.parentElement;
    if (!gallery || gallery.__atherZoom === cfgEl) return;
    gallery.__atherZoom = cfgEl;
    var cfg;
    try {
      cfg = JSON.parse(cfgEl.textContent);
    } catch (e) {
      return;
    }
    var scale = Math.min(2, Math.max(1.25, parseFloat(cfg.scale) || 1.5));
    var frames = [];

    function reset(f) {
      if (!f.classList.contains('is-zoomed')) return;
      var img = f.__img;
      f.classList.remove('is-zoomed');
      img.style.transform = '';
      f.setAttribute('aria-pressed', 'false');
      f.setAttribute('aria-label', cfg.label);
    }

    function resetAll(except) {
      frames.forEach(function (f) {
        if (f !== except) reset(f);
      });
    }

    function origin(f, x, y) {
      f.__ox = Math.min(100, Math.max(0, x));
      f.__oy = Math.min(100, Math.max(0, y));
      f.__img.style.transformOrigin = f.__ox + '% ' + f.__oy + '%';
    }

    function zoom(f, x, y) {
      resetAll(f);
      var img = f.__img;
      // a sharper source for the enlarged view: the browser picks it from srcset
      var want = Math.ceil(f.clientWidth * scale);
      if (img.srcset && (!img.__sized || img.__sized < want)) {
        img.sizes = want + 'px';
        img.__sized = want;
      }
      origin(f, x, y);
      img.style.transform = 'scale(' + scale + ')';
      f.classList.add('is-zoomed');
      f.setAttribute('aria-pressed', 'true');
      f.setAttribute('aria-label', cfg.reset);
    }

    function pointFrom(f, e) {
      var r = f.getBoundingClientRect();
      return [((e.clientX - r.left) / r.width) * 100, ((e.clientY - r.top) / r.height) * 100];
    }

    function init(f) {
      var img = f.querySelector('img.product-media__image, img');
      if (!img || f.__atherZoomReady) return;
      f.__atherZoomReady = true;
      f.__img = img;
      frames.push(f);
      f.classList.add('ather-zoom');
      f.tabIndex = 0;
      f.setAttribute('role', 'button');
      f.setAttribute('aria-pressed', 'false');
      f.setAttribute('aria-label', cfg.label);
      var hint = document.createElement('span');
      hint.className = 'ather-zoom__hint';
      hint.setAttribute('aria-hidden', 'true');
      hint.innerHTML =
        '<svg viewBox="0 0 24 24" width="18" height="18" focusable="false"><circle cx="10.5" cy="10.5" r="6" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M15 15l5 5M10.5 8v5M8 10.5h5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
      f.appendChild(hint);

      var drag = null; // touch / pen pan while zoomed
      var moved = false;

      // a click focuses the frame without the slider scrolling it into view (a sub-pixel jump)
      f.addEventListener('mousedown', function (e) {
        if (e.button !== 0) return;
        e.preventDefault();
        try {
          f.focus({ preventScroll: true });
        } catch (err) {}
      });

      f.addEventListener('click', function (e) {
        if (moved) {
          moved = false;
          return;
        }
        e.preventDefault();
        if (f.classList.contains('is-zoomed')) reset(f);
        else {
          var p = e.detail === 0 ? [50, 50] : pointFrom(f, e); // detail 0: keyboard-made click
          zoom(f, p[0], p[1]);
        }
      });

      f.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (f.classList.contains('is-zoomed')) reset(f);
          else zoom(f, 50, 50);
        } else if (e.key === 'Escape' && f.classList.contains('is-zoomed')) {
          e.preventDefault();
          e.stopPropagation();
          reset(f);
        } else if (f.classList.contains('is-zoomed')) {
          var step = { ArrowLeft: [-10, 0], ArrowRight: [10, 0], ArrowUp: [0, -10], ArrowDown: [0, 10] }[e.key];
          if (!step) return;
          e.preventDefault();
          e.stopPropagation();
          origin(f, f.__ox + step[0], f.__oy + step[1]);
        }
      });

      // mouse: the zoom point follows the pointer
      f.addEventListener('pointermove', function (e) {
        if (!f.classList.contains('is-zoomed')) return;
        if (e.pointerType === 'mouse') {
          var p = pointFrom(f, e);
          origin(f, p[0], p[1]);
          return;
        }
        if (!drag) return;
        e.stopPropagation();
        var r = f.getBoundingClientRect();
        var dx = e.clientX - drag.x, dy = e.clientY - drag.y;
        if (Math.abs(dx) + Math.abs(dy) > 4) moved = true;
        // dragging right shows more of the left side
        origin(f, drag.ox - (dx / r.width) * 100 * (scale / (scale - 1)) / scale, drag.oy - (dy / r.height) * 100 * (scale / (scale - 1)) / scale);
      });

      f.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse' || !f.classList.contains('is-zoomed')) return;
        e.stopPropagation(); // the carousel does not swipe while zoomed
        moved = false;
        drag = { x: e.clientX, y: e.clientY, ox: f.__ox, oy: f.__oy };
        try {
          f.setPointerCapture(e.pointerId);
        } catch (err) {}
      });
      var end = function (e) {
        if (!drag) return;
        drag = null;
        try {
          f.releasePointerCapture(e.pointerId);
        } catch (err) {}
      };
      f.addEventListener('pointerup', end);
      f.addEventListener('pointercancel', end);
      f.addEventListener('touchmove', function (e) {
        if (f.classList.contains('is-zoomed')) e.stopPropagation();
      }, { passive: true });
    }

    Array.prototype.forEach.call(gallery.querySelectorAll('.product-media-container--image'), init);

    // another image: back to normal
    var slides = gallery.querySelector('slideshow-slides');
    if (slides) {
      var lastLeft = slides.scrollLeft;
      slides.addEventListener(
        'scroll',
        function () {
          if (Math.abs(slides.scrollLeft - lastLeft) > 4) resetAll();
          lastLeft = slides.scrollLeft;
        },
        { passive: true }
      );
    }
    gallery.addEventListener('click', function (e) {
      if (e.target.closest('.slideshow-control, .slideshow-controls__thumbnail')) resetAll();
    });

    // off screen: back to normal
    if (typeof IntersectionObserver === 'function') {
      var w = document.querySelector('.page-wrapper');
      var root = w && /(auto|scroll)/.test(getComputedStyle(w).overflowY) ? w : null;
      new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) resetAll();
      }, { root: root }).observe(gallery);
    }
  }

  function scan() {
    Array.prototype.forEach.call(document.querySelectorAll(CONFIG), setup);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();

  // a variant change re-renders the section with fresh markup
  var queued = false;
  new MutationObserver(function () {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(function () {
      queued = false;
      scan();
    });
  }).observe(document.body, { childList: true, subtree: true });
})();
